/**
 * Sizers (`/sizers/:tab`).
 *
 * The pricing sizers. They lived under Admin (`/admin/sizers`) until
 * 2026-10-15, then moved to their own top-level section so non-admins in the
 * "Sizer Visibility" group can use them; SizersLayout supplies the chrome
 * (breadcrumb, sticky sub-tab switcher) and the access gate. The folder stays
 * under `admin/` to keep the move small.
 *
 *   rtl          → the RTL Sizer — size and price a fix & flip / ground-up
 *   guc          → the GUC Sizer v2: the SAME engine, endpoint and rules as the
 *                  rtl tab, restricted to ground-up and presenting a lean
 *                  results surface (GUC_SIZER_V2_FRONTEND_SPEC.md). The two
 *                  co-exist deliberately — rtl is the verbose instrument, guc
 *                  is the pricing surface — and neither replaces the other.
 *   max-leverage → the RTL / GUC quick check: class + per-class leverage grid,
 *                  deliberately WITHOUT a rate (POST /pricing/scenario/max_leverage)
 *   dscr         → the DSCR sizer, in either entry depth: the full form, or
 *                  Quick Entry (`?entry=quick`) — one rent override, no
 *                  provenance, for early shopping. ONE tab, because both post
 *                  the same endpoint and render the same answer; the switcher
 *                  lives under the page title (see DscrSizerTabs).
 *   quick-entry  → the old second tab, kept as a redirect so existing links,
 *                  bookmarks and pasted URLs still land somewhere correct.
 *   scenarios    → Priced Scenarios: every priced run the sizers persisted,
 *                  with its version history (GET/PUT /pricing/scenarios). The
 *                  read side of what the other tabs write.
 *
 * Every one of these owns its own page head and layout (`SizerShell`), so they
 * render bare here rather than inside `AdminScreenShell` — wrapping them would
 * stack a second title on top of theirs.
 */
import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

import SizerScreen from "../../SizerScreen/SizerScreen";
import GucSizerScreen from "../../GucSizerScreen/GucSizerScreen";
import DscrSizerTabs from "./DscrSizerTabs";
import { useCanUseDscrSizer } from "../../../hooks/useCanUseDscrSizer";
import MaxLeverageQuickCheck from "./MaxLeverageQuickCheck";
import PricedScenariosScreen from "./PricedScenariosScreen";

// The retired tab, preserved as a redirect onto the mode it became.
const QuickEntryRedirect = () => (
  <Navigate to="/sizers/dscr?entry=quick" replace />
);

const TABS = {
  rtl: SizerScreen,
  guc: GucSizerScreen,
  "max-leverage": MaxLeverageQuickCheck,
  dscr: DscrSizerTabs,
  "quick-entry": QuickEntryRedirect,
  scenarios: PricedScenariosScreen,
};

// The DSCR Sizer (both entry depths) is super-admin only — a typed URL for
// anyone else lands on Priced Scenarios.
const DSCR_TABS = ["dscr", "quick-entry"];

export default function AdminSizersScreen() {
  const { tab } = useParams();
  const canUseDscr = useCanUseDscrSizer();
  // Unknown tab falls back to the section landing page, same as bare
  // /sizers — a mistyped tab should show what exists, not a blank form.
  const Screen = useMemo(() => TABS[tab] || TABS.scenarios, [tab]);
  if (DSCR_TABS.includes(tab) && !canUseDscr) {
    return <Navigate to="/sizers/scenarios" replace />;
  }
  return <Screen />;
}
